import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces.js'
import {
  demoListHomeworkForClass,
  demoListSubmissionsForClass,
  demoListSubmissionsForHomework,
  demoListSubmissionsForLearner,
} from './eduDemo/eduDemoDirectReads.js'
import { scopedReadConstraints } from './accessScopeService'
import { uploadWorkspaceFiles } from './storageHubService'
import type {
  EduSubmission,
  EduSubmissionSource,
  EduSubmissionStatus,
} from '../types/schema.js'

export type {
  EduSubmission,
  EduSubmissionSource,
  EduSubmissionStatus,
}

export interface SetEduHomeworkInput {
  workspaceId: string
  checklistId: string
  dueDate: string
  maxScore?: number | null
  clear?: boolean
}

export interface SetEduHomeworkResult {
  success: boolean
  checklistId: string
}

export interface GetEduHomeworkRollInput {
  workspaceId: string
  checklistId: string
}

export interface HomeworkRollLearner {
  learnerId: string
  displayName: string
  rollNo?: string
  photoUrl?: string
  status: EduSubmissionStatus | 'ASSIGNED'
  frozen: boolean
  submission?: EduSubmission
}

export interface GetEduHomeworkRollResult {
  dueDate: string
  maxScore?: number | null
  learners: HomeworkRollLearner[]
}

export interface SubmitEduHomeworkInput {
  workspaceId: string
  checklistId: string
  learnerId: string
  note?: string
  fileIds?: string[]
  source: EduSubmissionSource
}

export interface SubmitEduHomeworkResult {
  submissionId: string
  status: EduSubmissionStatus
}

export interface ReviewEduHomeworkInput {
  workspaceId: string
  checklistId: string
  learnerId: string
  action: 'APPROVE' | 'RETURN'
  feedback?: string
  score?: number | null
}

export interface ReviewEduHomeworkResult {
  submissionId: string
  status: EduSubmissionStatus
}

export async function setEduHomework(
  input: SetEduHomeworkInput,
): Promise<SetEduHomeworkResult> {
  return callable<SetEduHomeworkInput, SetEduHomeworkResult>('setEduHomework', input)
}

export async function getEduHomeworkRoll(
  input: GetEduHomeworkRollInput,
): Promise<GetEduHomeworkRollResult> {
  return callable<GetEduHomeworkRollInput, GetEduHomeworkRollResult>('getEduHomeworkRoll', input)
}

export async function submitEduHomework(
  input: SubmitEduHomeworkInput,
): Promise<SubmitEduHomeworkResult> {
  return callable<SubmitEduHomeworkInput, SubmitEduHomeworkResult>('submitEduHomework', input)
}

export async function reviewEduHomework(
  input: ReviewEduHomeworkInput,
): Promise<ReviewEduHomeworkResult> {
  return callable<ReviewEduHomeworkInput, ReviewEduHomeworkResult>('reviewEduHomework', input)
}

export async function listSubmissionsForHomework(
  workspaceId: string,
  checklistId: string,
  restrictedUid?: string,
): Promise<EduSubmission[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSubmissionsForHomework(workspaceId, checklistId)
  }

  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('checklistId', '==', checklistId),
  ]

  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }

  const q = query(collection(firestoreDb, 'edu_submissions'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<EduSubmission, 'id'>),
  }))
}

export async function listSubmissionsForLearner(
  workspaceId: string,
  learnerId: string,
  from?: string,
  to?: string,
  restrictedUid?: string,
): Promise<EduSubmission[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSubmissionsForLearner(workspaceId, learnerId, from, to)
  }

  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('learnerId', '==', learnerId),
  ]

  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }

  if (from) {
    constraints.push(where('submittedAt', '>=', from))
  }

  if (to) {
    constraints.push(where('submittedAt', '<=', to))
  }

  constraints.push(orderBy('submittedAt', 'desc'))

  const q = query(collection(firestoreDb, 'edu_submissions'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<EduSubmission, 'id'>),
  }))
}

export async function listSubmissionsForClass(
  workspaceId: string,
  classId: string,
  restrictedUid?: string,
): Promise<EduSubmission[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListSubmissionsForClass(workspaceId, classId)
  }

  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('classId', '==', classId),
  ]

  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }

  constraints.push(orderBy('submittedAt', 'desc'))

  const q = query(collection(firestoreDb, 'edu_submissions'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<EduSubmission, 'id'>),
  }))
}

export interface HomeworkChecklistDoc {
  id: string
  workspaceId: string
  parentContainerId: string
  title: string
  description?: string
  eduKind?: 'HOMEWORK'
  eduHomework?: {
    dueDate: string
    maxScore?: number | null
    setBy?: string
    setAt?: unknown
  }
  scopeRead?: string[]
  [key: string]: unknown
}

/**
 * Queries all homework checklists for a given workspace and class subjects.
 * Chunks subject IDs by 30 to comply with Firestore 'in' query limits.
 * Includes scopedReadConstraints for restricted members.
 */
export async function listHomeworkForClass(
  workspaceId: string,
  subjectIds: string[],
): Promise<HomeworkChecklistDoc[]> {
  if (!workspaceId || subjectIds.length === 0) {
    return []
  }

  if (isEduDemoWorkspace(workspaceId)) {
    return demoListHomeworkForClass(workspaceId, subjectIds)
  }

  const chunks: string[][] = []
  for (let i = 0; i < subjectIds.length; i += 30) {
    chunks.push(subjectIds.slice(i, i + 30))
  }

  const scoped = scopedReadConstraints(workspaceId)
  const results: HomeworkChecklistDoc[] = []

  for (const chunk of chunks) {
    const q = query(
      collection(firestoreDb, 'checklists'),
      where('workspaceId', '==', workspaceId),
      where('eduKind', '==', 'HOMEWORK'),
      where('parentContainerId', 'in', chunk),
      ...scoped,
    )
    const snap = await getDocs(q)
    for (const d of snap.docs) {
      results.push({
        id: d.id,
        ...(d.data() as object),
      } as HomeworkChecklistDoc)
    }
  }

  return results
}

export interface UploadHomeworkFilesInput {
  workspaceId: string
  checklistId: string
  learnerId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

export async function uploadHomeworkFiles(
  input: UploadHomeworkFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_HOMEWORK',
    artifactId: `${input.checklistId}_${input.learnerId}`,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}

